import type { AccessControlProvider } from "@refinedev/core";

// Mirrors the backend Directus permission matrix for nav/route visibility
// (server still enforces). Three roles exist: Administrator (short-circuits
// true), Engineer, and Client. Empty list = admin-only. Unknown resource =
// hidden (fail closed).
//
// Client never mounts the staff Shell, so its entries here do not put anything
// in the staff nav; they say which resources the /portal screens are allowed to
// query. The disclosure boundary is the Directus Client policy, not this file.
export const STAFF_ROLES = [
  "Engineer",
  "Delivery Manager",
  "Project Manager",
  "Member",
  "Finance",
  "Submitter",
] as const;

export const RESOURCE_ROLES: Record<string, string[]> = {
  dashboard: [...STAFF_ROLES, "External Finance Vendor"],
  my_tasks: ["Engineer", "Delivery Manager", "Project Manager", "Member"],
  tasks: ["Engineer", "Delivery Manager", "Project Manager", "Member", "Finance", "Client"],
  clients: [...STAFF_ROLES, "External Finance Vendor", "Client"],
  routing: ["Engineer", "Delivery Manager", "Project Manager"],
  projects: [...STAFF_ROLES, "External Finance Vendor", "Client"],
  leads: ["Engineer", "Delivery Manager", "Project Manager", "Member"],
  contacts: ["Engineer", "Delivery Manager", "Project Manager", "Member"],
  documents: ["Engineer", "Project Manager", "Finance"],
  submit_expense: [...STAFF_ROLES],
  connections: [...STAFF_ROLES],
  expenses: ["Finance", "Engineer", "Delivery Manager", "Project Manager"],
  funding_buckets: ["Finance", "External Finance Vendor", "Engineer", "Delivery Manager", "Project Manager"],
  billable_line_items: ["Finance", "External Finance Vendor", "Engineer", "Delivery Manager", "Project Manager"],
  invoicing: ["Finance", "Engineer", "Delivery Manager"],
  cloud: ["Finance"],
  cloud_imports: ["Finance"],
  reports: ["Finance", "External Finance Vendor", "Engineer", "Delivery Manager", "Project Manager"],
  audit: [],
  access: [],
  email_settings: [],

  // Granting a person outside the firm sight of a client's projects is at
  // least as consequential as managing access inside it, so client_access sits
  // with `access` rather than with the engineer-facing resources. Revoking is
  // gated the same way: an engineer who can see a grant but cannot revoke it is
  // worse placed than one who cannot see it at all.
  client_access: [],

  // CHAT-01 4.6. The two collections holding workspace secrets (bot token,
  // signing secret) and the raw event log are admin-only; the three an engineer
  // acts on from the UI are theirs. chat_identities is the match table an admin
  // repairs, not something an engineer edits.

  // TKT-01. An engineer links tickets and holds their own credential; the sync
  // cursor is the poller's bookkeeping.
  ticket_links: ["Engineer", "Delivery Manager", "Project Manager", "Member"],
  ticket_credentials: [...STAFF_ROLES],
  ticket_sync_state: [],
  task_time_entries: [...STAFF_ROLES],

  // Per-user preferences (layout choices, etc.). Every staff role can read
  // and write their own row; the server filter scopes it to $CURRENT_USER.
  user_preferences: [...STAFF_ROLES, "External Finance Vendor"],
};

export const CLIENT_ROLE = "Client";
export const PORTAL_HOME = "/portal";

export function isClientRole(role: string) {
  return role === CLIENT_ROLE;
}

export function homeFor(role: string) {
  if (isClientRole(role)) return PORTAL_HOME;
  return role === "Engineer" ? "/my-tasks" : "/";
}

// Hides everything from an unsettled role: "" is not "Administrator" and no
// RESOURCE_ROLES entry lists it, so `allowed.includes("")` is false for every
// resource. web/e2e/nav-matrix.mjs leans on that -- a blank sidebar is how it
// tells "role has not loaded" from "role loaded and denied". Make this
// permissive for an empty role and every nav assertion there goes vacuous,
// with nothing turning red to say so.
export function roleCanSee(resource: string, role: string) {
  if (role === "Administrator") return true;
  const allowed = RESOURCE_ROLES[resource];
  if (!allowed) return false;
  return allowed.includes(role);
}

export const accessControlProvider: AccessControlProvider = {
  can: async ({ resource }) => {
    const role = localStorage.getItem("cordite_role") ?? "";
    return { can: roleCanSee(resource ?? "", role) };
  },
};
